import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { getOptionsWithGroupLabels } from '@/components/form/baseInputs/SelectInput/SelectInput.utils';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form/hooks';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { StateCode } from '@/types/schema';

import { STATE_TAX_SITUS_HELP_BODY } from '../../StructuredAssetsSubform/StructuredAssetsSubform.fields';
import {
  TAX_SITUS_GRANTOR_PREFIX,
  TAX_SITUS_STATE_PREFIX,
} from '../../StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  BasicAssetsSubformType,
  Fields,
  NamespaceType,
  SubformField,
} from '../BasicAssetsSubform.types';

interface AssetStateFieldProps {
  namespace: NamespaceType;
  secondNamespace: keyof BasicAssetsSubformType;
  hideLabel?: boolean;
  disabled?: boolean;
}

export const AssetStateField = ({
  namespace,
  secondNamespace,
  hideLabel,
  disabled,
}: AssetStateFieldProps) => {
  const { control } = useFormContext<Fields>();
  const { primaryClients } = useUnguardedHouseholdDetailsContext();

  const fieldName =
    `${namespace}.${secondNamespace}.taxSitus` as const satisfies SubformField;

  // Empty option only visible for legacy assets without a persisted situs.
  const currentValue = useWatch<Fields>({ control, name: fieldName });
  const showEmptyValue = !currentValue;

  const groupedOptions = useMemo(() => {
    const sortedClients = orderBy(primaryClients ?? [], [
      (c) => c.createdAt,
      (c) => c.id,
    ]);
    const grantorOptions: SelectInputOption[] = sortedClients.map((client) => {
      const statePrefix = client.stateCode ? `${client.stateCode} - ` : '';
      return {
        value: `${TAX_SITUS_GRANTOR_PREFIX}${client.id}`,
        display: `${statePrefix}(${client.displayGivenName}'s resident state)`,
      };
    });

    const stateCodeOptions: SelectInputOption[] = (
      Object.keys(STATE_CODE_TO_NAME) as StateCode[]
    ).map((stateCode) => ({
      value: `${TAX_SITUS_STATE_PREFIX}${stateCode}`,
      display: stateCode,
    }));

    return getOptionsWithGroupLabels([
      { label: 'State of domicile', options: grantorOptions },
      { label: null, options: stateCodeOptions },
    ]);
  }, [primaryClients]);

  return (
    <FormAwareSelectInput<Fields>
      fieldName={fieldName}
      label="State"
      hideLabel={hideLabel}
      control={control}
      options={groupedOptions}
      disabled={disabled}
      showEmptyValue={showEmptyValue}
      contextualHelp={<PopperContent body={STATE_TAX_SITUS_HELP_BODY} />}
    />
  );
};
